import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/Layout/AppLayout';
import { PersonAutocomplete } from '@/components/PersonForm/PersonAutocomplete';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { findRelationshipPath, type RelationshipPathResult, type PathStep, type EdgeType } from '@/api/relationshipPath.api';

// ─── Konstanten für die SVG-Visualisierung ──────────────────────────────────

const NODE_W = 160;
const NODE_H = 80;
const EDGE_W = 100;
const PADDING = 24;

// ─── SVG-Visualisierung des Pfades ──────────────────────────────────────────

function EdgeArrow({
  x,
  edgeType,
  label,
}: {
  x: number;
  edgeType: EdgeType;
  label: string;
}) {
  const color =
    edgeType === 'up' ? '#3b82f6' : edgeType === 'down' ? '#10b981' : '#8b5cf6';
  const midX = x + EDGE_W / 2;

  return (
    <g>
      {/* Linie */}
      <line
        x1={x}
        y1={NODE_H / 2}
        x2={x + EDGE_W}
        y2={NODE_H / 2}
        stroke={color}
        strokeWidth={2}
        markerEnd={`url(#arrow-${edgeType})`}
      />
      {/* Label */}
      <text
        x={midX}
        y={NODE_H / 2 - 6}
        textAnchor="middle"
        fontSize={10}
        fill={color}
        fontWeight="600"
      >
        {label}
      </text>
    </g>
  );
}

function PersonBox({
  x,
  step,
  isFirst,
  isLast,
}: {
  x: number;
  step: PathStep;
  isFirst: boolean;
  isLast: boolean;
}) {
  const { person } = step;
  const genderColor =
    person.gender === 'male'
      ? '#3b82f6'
      : person.gender === 'female'
        ? '#ec4899'
        : '#94a3b8';

  const highlight = isFirst || isLast;
  const bgColor = isFirst ? '#eff6ff' : isLast ? '#f0fdf4' : '#ffffff';
  const borderColor = isFirst ? '#3b82f6' : isLast ? '#10b981' : '#e2e8f0';
  const fullName = `${person.firstName}${person.lastName ? ' ' + person.lastName : ''}`;
  const dateStr = [person.birthDateText, person.deathDateText]
    .filter(Boolean)
    .join(' – ');

  // Name umbrechen wenn zu lang
  const nameLine1 =
    fullName.length > 18 ? fullName.slice(0, 17) + '…' : fullName;

  return (
    <g>
      {/* Box */}
      <rect
        x={x}
        y={0}
        width={NODE_W}
        height={NODE_H}
        rx={8}
        fill={bgColor}
        stroke={borderColor}
        strokeWidth={highlight ? 2 : 1.5}
        filter={highlight ? 'url(#shadow)' : undefined}
      />
      {/* Linker Farbstreifen (Geschlecht) */}
      <rect x={x} y={0} width={4} height={NODE_H} rx={2} fill={genderColor} />

      {/* Inhalt */}
      {/* Geschlecht-Symbol */}
      <text x={x + 14} y={22} fontSize={13} fill={genderColor} fontWeight="bold">
        {person.gender === 'male' ? '♂' : person.gender === 'female' ? '♀' : '◎'}
      </text>

      {/* Name */}
      <text
        x={x + 28}
        y={22}
        fontSize={12}
        fontWeight="600"
        fill="#1e293b"
      >
        {nameLine1}
      </text>

      {/* Label A / B */}
      {(isFirst || isLast) && (
        <text
          x={x + NODE_W - 10}
          y={16}
          fontSize={10}
          fontWeight="700"
          fill={isFirst ? '#3b82f6' : '#10b981'}
          textAnchor="end"
        >
          {isFirst ? 'A' : 'B'}
        </text>
      )}

      {/* Daten */}
      {dateStr && (
        <text x={x + 14} y={40} fontSize={10} fill="#64748b">
          {dateStr.length > 22 ? dateStr.slice(0, 21) + '…' : dateStr}
        </text>
      )}

      {/* Trennlinie */}
      <line
        x1={x + 8}
        y1={50}
        x2={x + NODE_W - 8}
        y2={50}
        stroke="#e2e8f0"
        strokeWidth={1}
      />

      {/* Generations-Info */}
      <text x={x + 14} y={66} fontSize={10} fill="#94a3b8">
        {person.birthDateText ? `* ${person.birthDateText}` : 'Geburtsdatum unbekannt'}
      </text>
    </g>
  );
}

function PathVisualization({ result }: { result: RelationshipPathResult }) {
  const { path } = result;
  if (path.length === 0) return null;

  const totalW =
    path.length * NODE_W + (path.length - 1) * EDGE_W + 2 * PADDING;
  const totalH = NODE_H + 2 * PADDING;

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <svg
        width={totalW}
        height={totalH}
        viewBox={`0 0 ${totalW} ${totalH}`}
        style={{ minWidth: totalW }}
      >
        {/* Definitionen: Pfeilspitzen + Schatten */}
        <defs>
          {(['up', 'down', 'partner'] as EdgeType[]).map((type) => {
            const color =
              type === 'up' ? '#3b82f6' : type === 'down' ? '#10b981' : '#8b5cf6';
            return (
              <marker
                key={type}
                id={`arrow-${type}`}
                markerWidth={8}
                markerHeight={8}
                refX={6}
                refY={3}
                orient="auto"
              >
                <path d="M0,0 L0,6 L8,3 z" fill={color} />
              </marker>
            );
          })}
          <filter id="shadow" x="-5%" y="-5%" width="110%" height="110%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>

        {/* Pfad-Elemente */}
        <g transform={`translate(${PADDING}, ${PADDING})`}>
          {path.map((step, idx) => {
            const nodeX = idx * (NODE_W + EDGE_W);
            const edgeX = nodeX + NODE_W;

            return (
              <g key={step.person.id}>
                <PersonBox
                  x={nodeX}
                  step={step}
                  isFirst={idx === 0}
                  isLast={idx === path.length - 1}
                />
                {step.edgeToNext && step.edgeLabelToNext && (
                  <EdgeArrow
                    x={edgeX}
                    edgeType={step.edgeToNext}
                    label={step.edgeLabelToNext}
                  />
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Legende */}
      <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
        <span className="font-medium text-slate-600">Legende:</span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-6 rounded bg-blue-500" />
          Aufwärts (Elternteil)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-6 rounded bg-emerald-500" />
          Abwärts (Kind)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-6 rounded bg-violet-500" />
          Partner/in
        </span>
      </div>
    </div>
  );
}

// ─── Schritt-für-Schritt Liste ───────────────────────────────────────────────

function PathStepsList({ path }: { path: PathStep[] }) {
  if (path.length <= 2) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">Schritt-für-Schritt</h3>
      <ol className="space-y-1.5">
        {path.map((step, idx) => {
          if (idx === path.length - 1) return null;
          const next = path[idx + 1];
          return (
            <li key={step.person.id} className="flex items-center gap-2 text-sm">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">
                {idx + 1}
              </span>
              <span className="font-medium text-slate-800">
                {step.person.firstName} {step.person.lastName ?? ''}
              </span>
              <span className="text-slate-400">→</span>
              <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                  step.edgeToNext === 'up'
                    ? 'bg-blue-50 text-blue-700'
                    : step.edgeToNext === 'down'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-violet-50 text-violet-700'
                }`}
              >
                {step.edgeLabelToNext}
              </span>
              <span className="text-slate-400">→</span>
              <span className="font-medium text-slate-800">
                {next.person.firstName} {next.person.lastName ?? ''}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ─── RelationshipFinderPage ─────────────────────────────────────────────────

export function RelationshipFinderPage() {
  const [personAId, setPersonAId] = useState<string | null>(null);
  const [personBId, setPersonBId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, isError, error } = useQuery<RelationshipPathResult>({
    queryKey: ['relationship-path', personAId, personBId],
    queryFn: () => findRelationshipPath(personAId!, personBId!),
    enabled: submitted && !!personAId && !!personBId,
  });

  const handleSearch = () => {
    if (personAId && personBId) {
      setSubmitted(true);
    }
  };

  const handleReset = () => {
    setPersonAId(null);
    setPersonBId(null);
    setSubmitted(false);
  };

  return (
    <AppLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800">Verwandtschafts-Pfadfinder</h1>
            <p className="mt-1 text-sm text-slate-500">
              Wähle zwei Personen aus dem Stammbaum – die App findet den kürzesten Verwandtschaftspfad zwischen ihnen.
            </p>
          </div>

          {/* Auswahl-Karte */}
          <div className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-4 sm:grid-cols-[1fr_auto_1fr_auto]">
              {/* Person A */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-blue-600">
                  Person A
                </label>
                <PersonAutocomplete
                  value={personAId}
                  onChange={(id) => { setPersonAId(id); setSubmitted(false); }}
                  placeholder="Person A suchen..."
                  excludeIds={personBId ? [personBId] : []}
                />
              </div>

              {/* Trennzeichen */}
              <div className="flex items-end pb-1.5">
                <div className="flex h-9 items-center px-2">
                  <svg className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0-4-4m4 4-4 4m0 6H4m0 0 4 4m-4-4 4-4" />
                  </svg>
                </div>
              </div>

              {/* Person B */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-emerald-600">
                  Person B
                </label>
                <PersonAutocomplete
                  value={personBId}
                  onChange={(id) => { setPersonBId(id); setSubmitted(false); }}
                  placeholder="Person B suchen..."
                  excludeIds={personAId ? [personAId] : []}
                />
              </div>

              {/* Button */}
              <div className="flex items-end gap-2">
                <button
                  onClick={handleSearch}
                  disabled={!personAId || !personBId || isLoading}
                  className="flex h-9 items-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-40"
                >
                  {isLoading ? (
                    <>
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Suche...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m9 20 3 3m0 0 3-3m-3 3V7m6 3 3-3m0 0-3-3m3 3H9" />
                      </svg>
                      Pfad finden
                    </>
                  )}
                </button>
                {submitted && (
                  <button
                    onClick={handleReset}
                    className="flex h-9 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-600 hover:bg-slate-50"
                    title="Zurücksetzen"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Laden */}
          {isLoading && (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" message="Verwandtschaftspfad wird berechnet..." />
            </div>
          )}

          {/* Fehler */}
          {isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <strong>Fehler:</strong>{' '}
              {error instanceof Error ? error.message : 'Unbekannter Fehler.'}
            </div>
          )}

          {/* Ergebnis */}
          {data && !isLoading && (
            <div className="space-y-4">
              {data.found ? (
                <>
                  {/* Beschreibungs-Badge */}
                  <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                      <svg className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Verwandtschaft gefunden</p>
                      <p className="mt-0.5 text-base font-medium text-slate-900">{data.description}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Kürzester Pfad: {data.path.length} {data.path.length === 1 ? 'Person' : 'Personen'},{' '}
                        {data.path.length - 1} {data.path.length - 1 === 1 ? 'Schritt' : 'Schritte'}
                      </p>
                    </div>
                  </div>

                  {/* Visualisierung */}
                  <div>
                    <h2 className="mb-2 text-sm font-semibold text-slate-600">Visualisierung des Pfades</h2>
                    <PathVisualization result={data} />
                  </div>

                  {/* Schritt-für-Schritt */}
                  <PathStepsList path={data.path} />
                </>
              ) : (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Kein Pfad gefunden</p>
                    <p className="mt-0.5 text-sm text-slate-700">{data.description}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Möglicherweise sind diese Personen nicht im selben Familienzweig verbunden.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hinweis wenn noch keine Suche */}
          {!submitted && !isLoading && (
            <div className="rounded-lg border border-dashed border-slate-200 p-10 text-center">
              <svg className="mx-auto mb-3 h-10 w-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
              </svg>
              <p className="text-sm text-slate-500">
                Wähle zwei Personen aus und klicke auf{' '}
                <span className="font-medium text-slate-700">„Pfad finden"</span>.
              </p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
